import React, { useCallback, useMemo, useState } from 'react'
import { Mention, MentionsInput } from 'react-mentions'
import {
  useCreateBlacklineCommentMutation,
  useGetDocumentByUuidQuery,
  useGetProjectPermissionsByIdQuery,
  useGetUserProfileQuery,
} from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { useSelector } from 'react-redux'
import { selectCurrentProject } from '../../redux/application-slice'
import { DocumentBlacklineSegment } from '../../shared/interfaces/project/document/blackline/document-blackline.interface'
import { useParams } from 'react-router-dom'
import { selectCommentWorkflowFilterStateQueryParams } from '../../redux/workflow-slice'
interface DiffCreateCommentComponentProps {
  segment: DocumentBlacklineSegment
}

const DiffCreateComment: React.FC<DiffCreateCommentComponentProps> = ({
  segment,
}) => {
  const renderSuggestion = useCallback((_, __, highlightedDisplay) => {
    return <div>{highlightedDisplay}</div>
  }, [])
  const { data: user } = useGetUserProfileQuery(undefined)
  const [comment, setComment] = useState<string>('')
  const [commentIsSaving, setCommentIsSaving] = useState<boolean>(false)
  const currentProject = useSelector(selectCurrentProject)
  const [createBlacklineComment] = useCreateBlacklineCommentMutation()
  const { documentId } = useParams()
  const { data: document } = useGetDocumentByUuidQuery(documentId ?? skipToken)
  const revisionQueryParams = useSelector(
    selectCommentWorkflowFilterStateQueryParams
  )

  const { data: permissions } = useGetProjectPermissionsByIdQuery(
    currentProject?.id ?? skipToken
  )

  const onSaveComment = useCallback(
    (e) => {
      e.stopPropagation()
      setCommentIsSaving(true)
      createBlacklineComment({
        document_blackline_segment: segment.id,
        comment,
        user_created: user,
        page: segment.page,
        document: document?.v1_document,
        documents: revisionQueryParams?.documentIds,
        project: revisionQueryParams?.projectId,
      })
      setCommentIsSaving(false)
      setComment('')
    },
    [
      comment,
      createBlacklineComment,
      document?.v1_document,
      segment.id,
      segment.page,
      revisionQueryParams?.documentIds,
      revisionQueryParams?.projectId,
      user,
    ]
  )

  const mentionsData = useMemo(() => {
    return (
      permissions?.users
        ?.filter?.((u) => u.email)
        ?.map((u) => {
          return {
            id: u.id,
            display: u.email,
          }
        }) ?? []
    )
  }, [permissions])

  const onChangeComment = useCallback((e) => {
    setComment(e.target.value)
  }, [])

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onSaveComment(e)
      }
    },
    [onSaveComment]
  )

  return (
    <div className={'space-y-2 py-1'}>
      <MentionsInput
        data-testid="segment-blackline-comment-input"
        className={'mentions'}
        placeholder="Write a comment"
        onChange={onChangeComment}
        onKeyDown={onKeyDown}
        value={comment}
        disabled={commentIsSaving}
      >
        <Mention
          trigger="@"
          appendSpaceOnAdd
          data={mentionsData}
          renderSuggestion={renderSuggestion}
        />
      </MentionsInput>
      {comment && (
        <div className={'flex justify-end gap-2'}>
          <button
            data-cy="segment-blackline-comment-save"
            type="button"
            className={`self-start rounded border ${!segment?.id ? 'border-gray-400 bg-gray-400' : 'border-indigo-600 bg-indigo-600'} px-4 py-1 text-sm font-semibold text-white shadow-sm ${!segment?.id ? 'cursor-not-allowed' : 'hover:bg-indigo-500'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
            onClick={onSaveComment}
            disabled={commentIsSaving || !segment?.id}
          >
            {commentIsSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  )
}

export default DiffCreateComment
